import { type ElementType, type ReactNode, type HTMLAttributes } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

// ── Variants ──────────────────────────────────────────────────────────────────

const typographyVariants = tv({
  base: 'text-uniform-gray-900 dark:text-uniform-gray-100',
  variants: {
    variant: {
      display: 'text-5xl font-bold leading-tight tracking-tight',
      title: 'text-3xl font-bold leading-tight',
      heading: 'text-xl font-semibold leading-snug',
      subheading: 'text-base font-semibold leading-snug',
      body: 'text-sm leading-relaxed',
      caption: 'text-xs leading-normal',
      label: 'text-xs font-medium uppercase tracking-wide'
    },
    muted: {
      true: 'text-uniform-gray-600 dark:text-uniform-gray-400'
    },
    weight: {
      thin: 'font-thin',
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extrabold: 'font-extrabold'
    }
  },
  defaultVariants: {
    variant: 'body'
  }
});

type TypographyVariants = VariantProps<typeof typographyVariants>;

// Default HTML element per variant
const defaultAs: Record<string, ElementType> = {
  display: 'h1',
  title: 'h2',
  heading: 'h3',
  subheading: 'h4',
  body: 'p',
  caption: 'span',
  label: 'span'
};

// ── Component ─────────────────────────────────────────────────────────────────

interface TypographyProps extends HTMLAttributes<HTMLElement> {
  variant?: 'display' | 'title' | 'heading' | 'subheading' | 'body' | 'caption' | 'label';
  /** Override the rendered HTML element */
  as?: ElementType;
  muted?: boolean;
  weight?: 'thin' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
  children?: ReactNode;
  className?: string;
}

function Typography({
  variant = 'body',
  as,
  muted,
  weight,
  children,
  className,
  ...rest
}: TypographyProps) {
  const Tag = as ?? defaultAs[variant] ?? 'p';
  return (
    <Tag {...rest} className={cn(typographyVariants({ variant, muted, weight }), className)}>
      {children}
    </Tag>
  );
}

export { Typography, typographyVariants };
export type { TypographyProps, TypographyVariants };
